import type { UsageResponse } from '@tallypg/contracts';
import { formatBytes, formatMoney, useApi } from '../api';

export function UsageChart({ usage }: { usage: UsageResponse }) {
  const max = Math.max(1, ...usage.points.map((p) => p.totalMicroUsd));
  if (usage.points.length === 0)
    return <p className="muted">No usage recorded yet. Usage appears within a few minutes of activity.</p>;
  return (
    <div>
      <div className="bars">
        {usage.points.map((p) => (
          <div
            key={p.period}
            className="bar"
            style={{ height: `${Math.max(2, (p.totalMicroUsd / max) * 100)}%` }}
            title={`${p.period}: ${formatMoney(p.totalMicroUsd, true)}`}
          />
        ))}
      </div>
      <div className="row between muted" style={{ fontSize: 11 }}>
        <span>{usage.points[0]?.period}</span>
        <span>{usage.points[usage.points.length - 1]?.period}</span>
      </div>
      <table style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th>Category</th>
            <th>Quantity</th>
            <th>Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Storage</td>
            <td>{(usage.totals.storageByteHours / 1_000_000_000).toFixed(3)} GB-hours</td>
            <td>{formatMoney(usage.totals.storageMicroUsd, true)}</td>
          </tr>
          <tr>
            <td>Database activity</td>
            <td>
              {(usage.totals.activeMs / 1000).toFixed(1)} s of query time ·{' '}
              {usage.totals.transactions.toLocaleString()} transactions ·{' '}
              {usage.totals.ioBlocks.toLocaleString()} data reads
            </td>
            <td>{formatMoney(usage.totals.activityMicroUsd, true)}</td>
          </tr>
          <tr>
            <td>Network transfer</td>
            <td>
              {formatBytes(usage.totals.egressBytes)} out · {formatBytes(usage.totals.ingressBytes)} in (free)
            </td>
            <td>{formatMoney(usage.totals.networkMicroUsd, true)}</td>
          </tr>
          <tr>
            <th>Total</th>
            <td />
            <th>{formatMoney(usage.totals.totalMicroUsd, true)}</th>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function UsagePage() {
  const usage = useApi<UsageResponse>('/usage');
  const pricing = useApi<{ version: number; categories: Record<string, string> }>('/pricing');
  return (
    <div>
      <h1>Usage</h1>
      <div className="card">
        <h2>Last 30 days</h2>
        {usage.data && <UsageChart usage={usage.data} />}
      </div>
      <div className="card">
        <h2>How pricing works</h2>
        <p className="muted">No plans, no instance sizes. You pay for three things, measured continuously:</p>
        <ul>
          <li>
            <strong>Storage</strong> — {pricing.data?.categories.storage}
          </li>
          <li>
            <strong>Database activity</strong> — {pricing.data?.categories.databaseActivity}
          </li>
          <li>
            <strong>Network transfer</strong> — {pricing.data?.categories.networkTransfer}
          </li>
        </ul>
        <p className="muted">
          An idle database costs only its storage: a 100 MB database is about three cents per month.
        </p>
      </div>
    </div>
  );
}
